import { useEffect, useMemo, useState } from "react";
import { SearchContext } from "./SearchContext";
import { trpc } from "../../../utils/trpc";
import { useQuery } from "../../hooks/useQuery";
import { useUser } from "../../hooks/useUser";

export const SearchProvider = ({ children }: { children: React.ReactNode }) => {
  const query = useQuery();
  const urlSearchQuery = query.get("search");

  const [searchQuery, setSearchQuery] = useState(urlSearchQuery ?? "");
  const { data: searchSuggestions, isLoading: relatedIsLoading } =
    trpc.searchSuggestions.useQuery(
      {
        search: searchQuery.replace(/^"|"$/g, ""),
      },
      {
        enabled: !!searchQuery.length,
      },
    );

  const [selectedPublications, setSelectedPublications] = useState<
    { id: string | number; title: string }[]
  >([]);
  useEffect(() => {
    if (urlSearchQuery) {
      setSearchQuery(urlSearchQuery);
    }
  }, [urlSearchQuery, setSearchQuery]);
  const { addNewRecentSearch } = useUser();

  const { data, isLoading, isFetched, fetchStatus } = trpc.searchAll.useQuery(
    {
      search: searchQuery,
      publicationIds: selectedPublications.map((p) => p.id),
    },
    {
      enabled: searchQuery.length > 2,
    },
  );

  // Invalidate the user when the searchQuery is fetched to get the latest data
  useEffect(() => {
    if (!isFetched) return;
    addNewRecentSearch({
      query: searchQuery,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  const context = useMemo(
    () => ({
      results: data,
      isLoading: isLoading || relatedIsLoading,
      isFetched,
      searchQuery,
      fetchStatus,
      setSearchQuery,
      selectedPublications,
      setSelectedPublications,
      relatedSearches:
        searchSuggestions?.map((search) => search.related ?? []).flat() ?? [],
    }),
    [
      data,
      isLoading,
      relatedIsLoading,
      isFetched,
      searchQuery,
      fetchStatus,
      selectedPublications,
      searchSuggestions,
    ],
  );

  return (
    <SearchContext.Provider value={context}>{children}</SearchContext.Provider>
  );
};
